<template>
  <div>
    <v-btn
      :disabled="disabled"
      @click.stop="setDialog(true)"
      :alt-text="`Delete ${attributeName}`"
      color="primary"
      outlined
      small
      :title="`Delete ${attributeName}`"
      >Delete
    </v-btn>

    <v-dialog
      v-model="dialog"
      :width="impactedEntityList.length ? '' : '500px'"
    >
      <v-card :loading="loading">
        <v-toolbar class="pa-0" elevation="0">
          <h2 class="text-center pt-1">
            Remove {{ upperFirst(parentEntityName) }} Attribute
          </h2>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false">
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
        </v-toolbar>
        <v-divider></v-divider>
        <template v-if="loading">
          <v-card-text>
            <GenericLoader
              >Loading effected {{ parentEntityName }}(s)...</GenericLoader
            >
          </v-card-text>
        </template>
        <template v-else>
          <template v-if="impactedEntityList.length">
            <v-card-text class="mt-4">
              <p class="mb-0 title">
                Removing "{{ attributeName }}" will clear values from
                {{ impactedEntityList.length }} {{ parentEntityName }}(s):
              </p>
              <v-data-table
                :headers="headers"
                :items="impactedEntityList"
                disable-pagination
                fixed-header
                height="400"
                hide-default-footer
              >
              </v-data-table>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="px-5 pb-0 d-flex align-start">
              <v-spacer></v-spacer>
              <v-text-field
                v-model="confirmationInput"
                autofocus
                outlined
                class="mr-5 mb-0"
                dense
                persistent-hint
                :hint="`Type \&quot;${attributeName}\&quot; to enable removal.`"
              ></v-text-field>
              <v-btn
                :disabled="confirmationInput !== attributeName"
                @click="entityRemove"
                class="mr-2 white--text"
                color="warning"
              >
                Remove Attribute
              </v-btn>
            </v-card-actions>
          </template>
          <template v-else>
            <v-card-text class="mt-4">
              <p class="title text-center">
                No {{ parentEntityName }}(s) have data saved to their "{{
                  attributeName
                }}" attribute.<br /><br />
                Removing this attribute is safe!
              </p>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                @click="entityRemove"
                class="mr-2 white--text"
                color="warning"
              >
                Remove Attribute
              </v-btn>
            </v-card-actions>
          </template>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import resolver from "../../lib/resolver";

import { mapActions, mapState, mapGetters } from "vuex";
import { upperFirst } from "lodash";
import GenericLoader from "@/components/GenericLoader";

export default {
  components: {
    GenericLoader,
  },
  computed: {
    // entityIdentifiers are gotten for the purpose of generating headers.
    // For example, I want an "email" column for users, but a "name" column for resources.
    entityIdentifierName() {
      const { parentEntityName } = this;
      const entityIdentifierName = this.attributeFilter({
        entityName: parentEntityName,
      }).find(({ importKey }) => importKey);
      return entityIdentifierName.name;
    },
    entityIdentifierLabel() {
      const { parentEntityName } = this;

      return this.attributeFilter({ entityName: parentEntityName }).find(
        ({ importKey }) => importKey
      ).label;
    },
    headers() {
      const {
        attributeName,
        entityIdentifierName,
        entityIdentifierLabel,
      } = this;
      return [
        { text: entityIdentifierLabel, value: entityIdentifierName },
        { text: "Organization", value: "organizationName" },
        { text: "Value Saved", value: attributeName },
      ];
    },
    impactedEntityList() {
      const { entityListBase, attributeName, organizationList } = this;

      // In the filter, I don't need to do array length checks BECAUSE FKs already got resolved by loadEntityList.
      // I only need to check if the attribute has a value.
      return entityListBase
        .filter(entity => !!entity[attributeName])
        .map(entity => {
          const entityOrganization = organizationList.find(
            ({ organizationId }) => organizationId === entity.organizationId
          );
          entity.organizationName = entityOrganization
            ? entityOrganization.name
            : "No Org Found!";
          return entity;
        });
    },
    ...mapGetters({
      attributeFilter: "attribute/filter",
    }),
    ...mapState({
      organizationList: state => state.organization.listAdmin,
    }),
  },
  data() {
    return {
      confirmationInput: "",
      dialog: false,
      loading: false,
      entityListBase: [],
    };
  },
  methods: {
    upperFirst,
    async entityRemove() {
      const { attributeName } = this;
      if (!attributeName) {
        return;
      }
      try {
        await this.$store.dispatch(
          `${this.attributeEntityName}/remove`,
          attributeName
        );
        this.confirmationInput = "";
        this.dialog = false;
        this.entityListBase = [];
        return this.$notifySuccess({ message: "Remove successful" });
      } catch (e) {
        console.error(e);
        return this.$notifyError({ message: "Unable to remove attribute" });
      }
    },
    async loadData() {
      this.confirmationInput = "";
      this.loading = true;

      try {
        await Promise.all([
          this.loadEntityList({ reload: false }),
          this.loadOrganizationList({ reload: false }),
          this.loadAttributeList({ reload: false }),
        ]);
      } catch (e) {
        console.error("Deletion confirm load fail:", e);
        this.$notifyError({
          message: "Could not load data. Remove attributes at your own risk.",
        });
      }

      this.loading = false;
    },
    async loadEntityList() {
      this.entityListBase = [];
      let result = [];

      try {
        result = (
          await axios({
            method: "get",
            url: "/api/admin/user",
          })
        ).data.result;
        this.entityListBase = result;
      } catch (e) {
        console.error("User Admin List load fail:", e);
        this.$notifyError({
          message: `Could not load list of ${this.parentEntityName}(s). Remove attributes at your own risk.`,
        });
        return;
      }

      //Now that we have the list of entities, they all need to be resolved-- in case the attribute being removed is a foreign key.
      this.entityListBase = await resolver.resolveEntityList(
        result,
        this.parentEntityName
      );

      //For each entity, check if there's a "Resolved" version of the attribute we're removing.
      //If so, replace the original version of the attribute (which is probably an id) with the readableText of the resolved version.
      const { attributeName } = this;
      const resolvedAttributeName = `${attributeName}Resolved`;

      this.entityListBase = this.entityListBase.map(entity => {
        if (!entity || !entity[resolvedAttributeName]) {
          return entity;
        }

        if (Array.isArray(entity[resolvedAttributeName])) {
          entity[attributeName] = entity[resolvedAttributeName]
            .map(({ readableText }) => readableText)
            .join(", ");
          return entity;
        }

        entity[attributeName] = entity[resolvedAttributeName].readableText;
        return entity;
      });
    },
    async setDialog(val) {
      this.dialog = val;
      if (val === true) {
        await this.loadData();
      }
    },
    ...mapActions({
      loadOrganizationList: "organization/listAdmin",
      loadAttributeList: "attribute/list",
    }),
  },
  props: {
    attributeEntityName: {
      required: true,
      type: String,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    attributeName: {
      type: String,
    },
    parentEntityName: {
      required: true,
      type: String,
    },
  },
};
</script>
